import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Typography, Link } from '@mui/material';

interface MarkdownMessageProps {
  content: string;
  mentions?: Array<{ uid: string; name: string }>;
  isOwn?: boolean;
  fontSize?: number;
}

/**
 * Renders a chat message with Markdown support + mention highlighting.
 * Plain text messages render identically to before (no visual regressions).
 * Supports tables, bold, italic, lists, links, code from Markdown.
 */
const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ content, mentions = [], isOwn = false, fontSize = 13 }) => {
  // Check if content has any markdown syntax — if not, use fast path with mention highlighting
  const hasMarkdown = /[|*_`#\-\[\]>]/.test(content) && (
    /\|.*\|/.test(content) ||        // tables
    /\*\*.+\*\*/.test(content) ||    // bold
    /_.+_/.test(content) ||          // italic
    /^#{1,6}\s/m.test(content) ||    // headings
    /^[-*]\s/m.test(content) ||      // lists
    /^\d+\.\s/m.test(content) ||     // ordered lists
    /```/.test(content) ||           // code blocks
    /`[^`]+`/.test(content) ||       // inline code
    /^\>/m.test(content) ||          // blockquotes
    /\[.+\]\(.+\)/.test(content)     // links
  );

  if (!hasMarkdown) {
    return renderPlainWithMentions(content, mentions, isOwn, fontSize);
  }

  // Process mentions in markdown content by wrapping them in bold+highlight
  let processed = content;
  if (mentions.length > 0) {
    const mentionNames = mentions.map(m => m.name).filter(Boolean);
    mentionNames.forEach(name => {
      processed = processed.replace(
        new RegExp(`@${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g'),
        `**@${name}**`
      );
    });
  }

  return (
    <Box sx={{
      wordBreak: 'break-word',
      fontSize,
      overflow: 'hidden', maxWidth: '100%', minWidth: 0,
      '& p': { m: 0, mb: 0.5, '&:last-child': { mb: 0 } },
      '& table': {
        borderCollapse: 'collapse', fontSize: '0.8em', my: 0.5,
        maxWidth: '100%', display: 'block', overflowX: 'auto',
      },
      '& th, & td': {
        border: '1px solid', borderColor: isOwn ? 'rgba(255,255,255,0.3)' : 'grey.300',
        px: 1, py: 0.5, fontSize: 'inherit', whiteSpace: 'nowrap',
      },
      '& th': { fontWeight: 600, bgcolor: isOwn ? 'rgba(255,255,255,0.1)' : 'grey.100' },
      '& strong': { fontWeight: 600 },
      '& em': { fontStyle: 'italic' },
      '& ul, & ol': { m: 0, pl: 2.5, mb: 0.5 },
      '& li': { fontSize: 'inherit', mb: 0.25 },
      '& code': {
        fontFamily: 'monospace', fontSize: '0.9em',
        bgcolor: isOwn ? 'rgba(255,255,255,0.15)' : 'grey.100',
        px: 0.5, py: 0.15, borderRadius: 0.5,
      },
      '& pre': {
        bgcolor: isOwn ? 'rgba(255,255,255,0.1)' : 'grey.100',
        p: 1, borderRadius: 1, overflowX: 'auto', my: 0.5,
        '& code': { bgcolor: 'transparent', p: 0 },
      },
      '& blockquote': {
        borderLeft: '3px solid', borderColor: isOwn ? 'rgba(255,255,255,0.4)' : 'grey.400',
        pl: 1.5, ml: 0, my: 0.5, fontStyle: 'italic', opacity: 0.85,
      },
      '& a': { color: isOwn ? 'inherit' : 'primary.main', textDecoration: 'underline' },
      '& hr': { border: 'none', borderTop: '1px solid', borderColor: isOwn ? 'rgba(255,255,255,0.2)' : 'grey.300', my: 0.5 },
    }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => (
            <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', textDecoration: 'underline' }}>
              {children}
            </Link>
          ),
          p: ({ children }) => <Typography variant="body2" component="p" sx={{ fontSize: 'inherit', lineHeight: 1.5 }}>{children}</Typography>,
          // Mentions were pre-processed above into `**@Name**`. If we see a
          // bold node that looks like `@Name`, render it as a highlighted
          // mention pill and mark it `data-no-translate` so the DOM
          // translator leaves the name alone.
          strong: ({ children }) => {
            const text = React.Children.toArray(children).map((c) => (typeof c === 'string' ? c : '')).join('');
            if (text.startsWith('@')) {
              return (
                <Box
                  component="span"
                  data-no-translate="true"
                  sx={{
                    bgcolor: isOwn ? 'rgba(255,255,255,0.25)' : 'primary.light',
                    color: isOwn ? 'white' : 'primary.main',
                    borderRadius: 0.5,
                    px: 0.5,
                    fontWeight: 600,
                    fontSize: 'inherit'
                  }}
                >
                  {children}
                </Box>
              );
            }
            return <strong>{children}</strong>;
          }
        }}
      >
        {processed}
      </ReactMarkdown>
    </Box>
  );
};

/**
 * Fast path for plain text messages with mention highlighting (no markdown).
 * This matches the existing rendering behavior exactly.
 */
function renderPlainWithMentions(
  text: string,
  mentions: Array<{ uid: string; name: string }>,
  isOwn: boolean,
  fontSize: number
) {
  if (!mentions.length) {
    return <Typography variant="body2" sx={{ wordBreak: 'break-word', fontSize, whiteSpace: 'pre-wrap' }}>{text}</Typography>;
  }

  const mentionNames = mentions.map(m => m.name).filter(Boolean);
  if (!mentionNames.length) {
    return <Typography variant="body2" sx={{ wordBreak: 'break-word', fontSize, whiteSpace: 'pre-wrap' }}>{text}</Typography>;
  }

  const escaped = mentionNames.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(@(?:${escaped.join('|')}))`, 'g');
  const parts = text.split(regex);

  return (
    <Typography variant="body2" sx={{ wordBreak: 'break-word', fontSize, whiteSpace: 'pre-wrap' }}>
      {parts.map((part, i) => {
        if (part.startsWith('@') && mentionNames.some(n => part === `@${n}`)) {
          return (
            <Box key={i} component="span" data-no-translate="true" sx={{
              bgcolor: isOwn ? 'rgba(255,255,255,0.25)' : 'primary.light',
              color: isOwn ? 'white' : 'primary.main',
              borderRadius: 0.5, px: 0.5, fontWeight: 600, fontSize: 'inherit',
            }}>
              {part}
            </Box>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </Typography>
  );
}

export default MarkdownMessage;
